import { assert } from '@gitbook/util-ts';

import { TagValidationError } from '../../tags';
import type { Revision, RevisionTags } from '../../types';
import { addUserEdits, mutations, yToJSON } from '../../utils';

/**
 * Nest a tag under a parent tag, or unnest it with `null`. Nesting is one level deep and stays
 * within a category: the parent must be a top-level tag in the tag's own category.
 */
export function setTagParentInRevision(
    revision: mutations.MutableData<Revision>,
    slug: string,
    parent: string | null,
    authors: string[] = []
): void {
    const tags = mutations.get(revision, 'tags');
    const all: RevisionTags = yToJSON(tags) ?? {};
    const tag = tags && mutations.get(tags, slug);
    assert(tag && Object.hasOwn(all, slug), new TagValidationError('Tag does not exist'));
    if (parent === null) {
        mutations.del(tag, 'parent');
    } else {
        assert(parent !== slug, new TagValidationError('A tag cannot be its own parent'));
        assert(Object.hasOwn(all, parent), new TagValidationError('Parent tag does not exist'));
        const parentTag = all[parent];
        assert(
            parentTag.parent === undefined,
            new TagValidationError('Child tags cannot have child tags')
        );
        assert(
            !Object.values(all).some((other) => other.parent === slug),
            new TagValidationError('A tag that has child tags cannot become a child tag')
        );
        assert(
            parentTag.group === all[slug].group,
            new TagValidationError('A child tag must be in the same category as its parent')
        );
        mutations.set(tag, 'parent', parent);
    }
    if (authors.length) {
        mutations.set(tag, 'edits', addUserEdits(mutations.get(tag, 'edits'), authors));
    }
}

/**
 * After concurrent edits or a merge, un-nest any child tag whose parent is gone, is the tag
 * itself, is nested, or is in another category. The child stays, as a top-level tag. When two
 * nestings conflict, slug order (not edit order) decides which one is undone.
 */
export function normalizeTagParentsInRevision(revision: mutations.MutableData<Revision>): boolean {
    const tags = mutations.get(revision, 'tags');
    if (!tags) return false;
    const all: RevisionTags = yToJSON(tags);
    let changed = false;
    // Sorted, and reading the repaired state, so the result is the same on every client.
    for (const slug of Object.keys(all).sort()) {
        const parent = all[slug].parent;
        if (parent === undefined) continue;
        const parentTag = Object.hasOwn(all, parent) ? all[parent] : undefined;
        if (
            !parentTag ||
            parent === slug ||
            parentTag.parent !== undefined ||
            parentTag.group !== all[slug].group
        ) {
            delete all[slug].parent;
            mutations.del(mutations.get(tags, slug)!, 'parent');
            changed = true;
        }
    }
    return changed;
}
